import React, { useState } from 'react';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AuthError from '../../AuthError/authError';
import styles from '../AuthInput/authInputs.module.scss';

interface ICustomTimePickerProps {
    label?: string;
    value: Date | null;
    onChange: (value: Date | null) => void;
    error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
    disabled?: boolean;
    id?: string;
    showTextError?: boolean;
    placeholder?: string;
    style?: string;
    labelStyle?: string;
    startTime?: string | null;
    endTime?: string | null;
    selectedDate?: string | null; // Date in YYYY-MM-DD format
}

const CustomTimePicker: React.FC<ICustomTimePickerProps> = ({
    label,
    value,
    onChange,
    error,
    disabled,
    id,
    showTextError = true,
    placeholder,
    style,
    labelStyle,
    startTime,
    endTime,
    selectedDate
}) => {
    const [open, setOpen] = useState<boolean>(false);
    const parseTime = (timeStr?: string | null): Date | undefined => {
        if (!timeStr) return undefined;
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        const now = new Date();
        now.setHours(hours, minutes, seconds || 0, 0);
        return now;
    };

    // Check if selected date is today
    const isToday = (): boolean => {
        if (!selectedDate) return false;
        const today = new Date();
        const selected = new Date(selectedDate);
        return (
            today.getFullYear() === selected.getFullYear() &&
            today.getMonth() === selected.getMonth() &&
            today.getDate() === selected.getDate()
        );
    };

    // Get minimum time: if today, use current time (rounded up to next minute), otherwise use startTime
    const getMinTime = (): Date | undefined => {
        if (isToday()) {
            const now = new Date();
            // Round up to the next minute to prevent selecting the current minute
            now.setMinutes(now.getMinutes() + 1, 0, 0);
            return now;
        }
        return startTime ? parseTime(startTime) : undefined;
    };

    const minTime = getMinTime();
    const maxTime = endTime ? parseTime(endTime) : undefined;

    return (
        <div className={`${styles.container} ${style}`}>
            {label && (
                <label htmlFor={id} className={`${styles.label} ${labelStyle}`}>
                    {label}
                    {error && <span style={{ color: '#b00020', marginLeft: '5px' }}>*</span>}
                </label>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopTimePicker
                    disabled={disabled}
                    value={value}
                    onChange={onChange}
                    ampm={false}
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    views={['hours', 'minutes']}
                    minTime={minTime}
                    maxTime={maxTime}
                    slotProps={{
                        textField: {
                            placeholder,
                            id,
                            fullWidth: true,
                            error: !!error,
                            helperText: '',
                            onClick: (e) => {
                                e.stopPropagation();
                                setOpen(true)
                            },
                            inputProps: {
                                readOnly: true,
                                helperText: '',
                                placeholder: '',
                            },
                            sx: {
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'transparent' },
                                    '&:hover fieldset': { borderColor: 'var(--text-dark)' },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--text-dark)',
                                        borderWidth: '1px',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: 'var(--input-label)',
                                        transform: "translateX(-5px)"
                                    },
                                    ...(error && {
                                        '& fieldset': { borderColor: '#b00020' },
                                    }),
                                    '& .MuiInputBase-input': {
                                        padding: '16px 10px',
                                        fontSize: '13px',
                                        fontWeight: "400",
                                        '@media screen and (max-height:800px)': {
                                            padding: '10px 5px',
                                        },
                                          '@media screen and (max-width:800px)': {
                                            padding: '10px 5px',
                                        },
                                        '@media (max-width: 400px)': {
                                            padding: '12px 5px',
                                            fontSize: '12px'
                                        },

                                    },
                                },
                            },
                        },
                        popper: {
                            sx: {
                                '& .MuiPickersLayout-root': {
                                    backgroundColor: 'var(--background-color)',

                                    '& .MuiButtonBase-root': {
                                        color: 'var(--text-dark)',
                                    },
                                },
                                '& .MuiButtonBase-root.Mui-selected': {
                                    backgroundColor: 'var(--primary-hover) !important',
                                    color: 'white !important',
                                },
                                '& .MuiClock-root': {
                                    backgroundColor: 'var(--background-color)',
                                    borderRadius: '16px',
                                },
                                '& .MuiClockNumber-root': {
                                    color: 'var(--text-dark)',
                                    fontSize: '14px',
                                    '&.Mui-selected': {
                                        backgroundColor: 'var(--primary-hover)',
                                        color: '#fff',
                                    },
                                },
                                '& .MuiClockPointer-root': {
                                    backgroundColor: 'var(--primary-hover)',
                                },
                                '& .MuiClockPointer-thumb': {
                                    borderColor: 'var(--primary-hover)',
                                    backgroundColor: 'var(--text-dark)',
                                },
                                '& .MuiClock-pin': {
                                    backgroundColor: 'var(--text-dark)',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.2)',
                                },
                            },
                        },
                    }}
                />
            </LocalizationProvider>
            {showTextError && <AuthError text={error?.message} />}
        </div>
    );
};

export default CustomTimePicker;
